// src/lib/travelCheck.js
//
// Reservations-vs-dispatch cross-check (Jill 2026-08-05). DISPATCH IS THE
// TRUTH: the office's travel_reservations sheet should agree with the travel
// the dispatch sheet shows for each pilot. Laura may be mid-booking (missing
// is normal early), or a reservation may contradict dispatch (the dangerous
// case) — either way the pilot should see it before travel day.
//
// Finding types, one per problem per day:
//   missing  — dispatch shows a travel leg, no matching reservation on file
//   mismatch — reservation(s) exist that day but the route disagrees
//   no_hotel — dispatch shows an overnight (> code) with no hotel coverage
//   extra    — a reservation is on file for a day dispatch shows no travel
//
// Travel-code decode per data/dispatch/travelcodes.csv (k = Kake, NOT
// Ketchikan). Letters BEFORE the T are the origin, AFTER it the destination;
// "am" = morning; trailing ">" = overnight layover. Codes are lowercase in
// the cells (ships/ports are ALL-CAPS), which is what keeps "SIT" the port
// from reading as travel — never parse an all-caps token.

import { hasHotel, computeHotelOvernight } from "./hotelOvernight.js";
import { normalizeLoc } from "./travelNormalize.js";

export const TRAVEL_LOC = {
  j: "JNU", c: "KTN", e: "ELF", s: "SIT", k: "KAKE", kl: "KLW",
  y: "YAK", i: "ISP", gi: "GIS", w: "SKG", r: "WRG",
};

// Reservation from/to fields are usually codes, occasionally prose. The place
// aliases live in travelNormalize.js so the check and the Travel board agree on
// what counts as the same place. Unknown spellings stay as-is and will read as
// a mismatch — over-nag, never a silent miss (a typo like "KAE" deserves eyes).
export const normLoc = normalizeLoc;

// two-letter codes before one-letter so "kl"/"gi" win over "k"/"g"
const LETTERS = "gi|kl|j|c|e|s|k|y|i|w|r";
const RUN_RE = new RegExp(`^(am)?(${LETTERS})?t(${LETTERS})?(>)?$`);
const LAYOVER_RE = new RegExp(`^(am)?(${LETTERS})(>)$`);

// Decode one cell's travel facts. Handoff/assignment cells carry travel
// tokens next to ship codes ("COR SKG wTj VOS"); all-caps tokens are ships
// or ports and never parsed. Tokens we can't read ("cruise", "ENDofkT") are
// skipped — a TRAVEL-status cell that yields nothing readable still gets an
// existence check via hasT when a bare T is present.
export function parseCellTravel(rawCell) {
  const facts = { runs: [], layoverAt: null, hasT: false };
  if (!rawCell) return facts;
  const toks = rawCell.replace(/\s+>/g, ">").trim().split(/\s+/);
  for (const tok of toks) {
    if (tok === "T") { facts.hasT = true; continue; }
    if (tok === tok.toUpperCase()) continue; // ship/port/meeting token
    const t = tok.toLowerCase();
    let m = t.match(RUN_RE);
    if (m && (m[2] || m[3])) {
      facts.hasT = true;
      facts.runs.push({
        from: TRAVEL_LOC[m[2]] || null,
        to: TRAVEL_LOC[m[3]] || null,
        overnight: !!m[4],
      });
      continue;
    }
    m = t.match(LAYOVER_RE);
    if (m) facts.layoverAt = TRAVEL_LOC[m[2]] || null;
  }
  return facts;
}

const iso10 = (v) => (v || "").slice(0, 10);
// a reservation row that represents a travel movement (not a PS-off checkout,
// not a hotel-only arrival note)
const isRunRow = (r) => !r.is_ps_off && !!(r.from_loc || r.to_loc || r.carrier || r.flight);
const runRowLabel = (r) =>
  [normLoc(r.from_loc) || "?", "to", normLoc(r.to_loc) || "?", r.flight ? `(${r.flight})` : ""].join(" ").trim();

// myDispatchRows: the signed-in pilot's rows from the CURRENT sheet, from a
//   couple of weeks back through the end of the sheet (past rows feed hotel
//   checkout coverage), any cell_status.
// myReservations: the pilot's travel_reservations rows, NOT cancelled.
// today: ISO yyyy-mm-dd. Findings are only raised for today onward.
export function travelCheckFindings(myDispatchRows, myReservations, today) {
  const res = (myReservations || []).filter((r) => !r.is_cancelled);
  const resByDate = new Map();
  for (const r of res) {
    const d = iso10(r.travel_date);
    if (!resByDate.has(d)) resByDate.set(d, []);
    resByDate.get(d).push(r);
  }
  const assignedRows = (myDispatchRows || []).filter((r) => r.cell_status === "ASSIGNED");

  // one parse per distinct raw_cell per day (handoff cells emit 2 rows)
  const byDate = new Map();
  for (const row of myDispatchRows || []) {
    const d = iso10(row.duty_date);
    if (d < today) continue;
    if (!byDate.has(d)) byDate.set(d, new Set());
    byDate.get(d).add(row.raw_cell || "");
  }

  const hotelCovered = (date) => {
    if (computeHotelOvernight(res, assignedRows, date).length > 0) return true;
    // a same-day "SELF" stay is the pilot's own arrangement — handled
    return (resByDate.get(date) || []).some(
      (r) => (r.hotel || "").trim().toUpperCase() === "SELF");
  };

  const findings = [];
  for (const [date, cells] of [...byDate.entries()].sort()) {
    const facts = { runs: [], layoverAt: null, hasT: false };
    for (const cell of cells) {
      const f = parseCellTravel(cell);
      facts.runs.push(...f.runs);
      facts.hasT = facts.hasT || f.hasT;
      facts.layoverAt = facts.layoverAt || f.layoverAt;
    }
    const runRows = (resByDate.get(date) || []).filter(isRunRow);

    let overnightHere = null;
    for (const run of facts.runs) {
      if (run.overnight) overnightHere = run.to || overnightHere || "?";
      const matched = runRows.some(
        (r) => (!run.from || normLoc(r.from_loc) === run.from) &&
               (!run.to || normLoc(r.to_loc) === run.to));
      if (matched) continue;
      if (runRows.length === 0) {
        findings.push({ type: "missing", date, run });
      } else {
        findings.push({ type: "mismatch", date, run, onFile: runRows.map(runRowLabel) });
      }
    }
    // bare T with no readable route: just make sure SOMETHING is booked
    if (facts.hasT && facts.runs.length === 0 && runRows.length === 0) {
      findings.push({ type: "missing", date, run: { from: null, to: null } });
    }
    // overnights: trailing ">" on a run (night at the destination) or a
    // layover-only cell like "k>" (night mid-journey)
    const nightAt = facts.layoverAt || overnightHere;
    if ((facts.layoverAt || overnightHere) && !hotelCovered(date)) {
      findings.push({ type: "no_hotel", date, place: nightAt || "?" });
    }
    // reservation on a day dispatch doesn't show travel — dispatch is the truth
    if (!facts.hasT && runRows.length > 0) {
      findings.push({ type: "extra", date, onFile: runRows.map(runRowLabel) });
    }
  }
  return findings;
}

// Human line per finding, for the My Travel panel and anywhere else.
export function findingText(f) {
  const route = (run) =>
    run.from && run.to ? `${run.from} to ${run.to}` :
    run.from ? `from ${run.from}` : run.to ? `to ${run.to}` : "(route not readable)";
  switch (f.type) {
    case "missing":
      return `Dispatch has you traveling ${route(f.run)}, but no reservation is on file yet.`;
    case "mismatch":
      return `Dispatch shows travel ${route(f.run)}; on file: ${f.onFile.join("; ")}. Dispatch is the truth; check with the office.`;
    case "no_hotel":
      return `Dispatch shows an overnight${f.place && f.place !== "?" ? ` in ${f.place}` : ""}, but no hotel is on file for that night.`;
    case "extra":
      return `A reservation is on file (${f.onFile.join("; ")}), but dispatch doesn't show travel that day.`;
    default:
      return "";
  }
}

export { hasHotel };
